/**
 * MobileContextMenu - long-press context menu for mobile icon grid
 *
 * Shows "Move to App Library" on home pages, "Add to Home Screen" in App Library.
 * Frosted glass styling matching IconContextMenu.
 */

"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface MobileContextMenuProps {
  position: { x: number; y: number };
  iconLabel: string;
  isInAppLibrary: boolean;
  onRemoveFromHomeScreen: () => void;
  onAddToHomeScreen: () => void;
  onClose: () => void;
}

export function MobileContextMenu({
  position,
  iconLabel,
  isInAppLibrary,
  onRemoveFromHomeScreen,
  onAddToHomeScreen,
  onClose,
}: MobileContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState(position);

  // Close on touch outside
  useEffect(() => {
    const handleTouch = (e: TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("touchstart", handleTouch);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("touchstart", handleTouch);
    };
  }, [onClose]);

  // Clamp to viewport
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    let x = position.x - rect.width / 2; // center horizontally on touch
    let y = position.y - rect.height - 12; // position above touch point

    // Clamp horizontal
    if (x + rect.width > vw - 8) x = vw - rect.width - 8;
    if (x < 8) x = 8;

    // If not enough room above, position below
    if (y < 8) y = position.y + 20;

    setAdjustedPos({ x, y });
  }, [position]);

  const handleAction = useCallback(() => {
    if (isInAppLibrary) {
      onAddToHomeScreen();
    } else {
      onRemoveFromHomeScreen();
    }
    onClose();
  }, [isInAppLibrary, onRemoveFromHomeScreen, onAddToHomeScreen, onClose]);

  return (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        left: adjustedPos.x,
        top: adjustedPos.y,
        zIndex: 10000,
        minWidth: 200,
      }}
      className="overflow-hidden rounded-xl shadow-2xl"
      role="menu"
    >
      <div
        className="py-1"
        style={{
          backgroundColor: "rgba(40, 40, 45, 0.92)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          borderRadius: 12,
        }}
      >
        <div
          className="px-4 py-2 text-xs font-medium"
          style={{ color: "rgba(255, 255, 255, 0.5)" }}
        >
          {iconLabel}
        </div>
        <div
          style={{
            height: 1,
            margin: "0 8px",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <button
          className="w-full px-4 py-2.5 text-left text-sm transition-colors active:bg-white/10"
          style={{ color: "#e4e4e7" }}
          onClick={handleAction}
          role="menuitem"
        >
          {isInAppLibrary ? "Add to Home Screen" : "Move to App Library"}
        </button>
      </div>
    </div>
  );
}
